import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../static/css/PublicEvents.css';
import { resolveEventArtwork } from './vibeArt';

// The public "Upcoming Events" block. One component, rendered in two places: Welcome.js (which
// serves BOTH the signed-out and signed-in splash, and /coming-soon) and Home.js (the logged-in
// home). It is deliberately identical in every one of those cases — GET /public/events is
// unauthenticated, no Authorization header and no credentials, so there is nothing to branch on.
//
// Three things the design wireframe shows are absent on purpose, and re-adding them is a bug:
//   - No attendee faces. The endpoint sends no attendee identities, ever — only attendee_count.
//     Placeholder faces next to a real count would read as deceptive.
//   - No distance chip. An anonymous visitor has no location to measure from.
//   - No "See all events" link. /events does not exist yet; a link to a 404 is worse than none.
//
// 🔒 THE WHOLE CARD IS THE LINK, and that is deliberate — the circular arrow was removed precisely so
// the entire card is the target. The card element IS the <Link>, rather than a wrapper around one, so
// there is no inner box with a smaller hit area. It points at /e/{slug} (App.js, EventDetail.js).
// A real <Link> also supplies the pointer cursor, keyboard focus and middle-click for free, which is
// why the CSS no longer sets `cursor: pointer` by hand. `user-select: none` stays: dragging across a
// card should not select its text.
class PublicEvents extends Component {
    constructor() {
        super();
        this.state = { status: 'loading', items: [] };
    }

    // "2026-10-14" -> { month: 'OCT', day: '14' }, and null if the date can't be read (the iOS chip
    // renders nothing in that case too). Parsed by splitting the string, never with `new Date(...)`:
    // `new Date('2026-10-14')` is UTC midnight, which renders as the 13th in every negative-offset
    // timezone — the same off-by-one that bit birthdate display.
    dateParts = (iso) => {
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
        if (!match) return null;
        const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const month = months[Number(match[2]) - 1];
        const day = Number(match[3]);
        if (!month || !day) return null;
        return { month: month, day: String(day) };
    };

    componentDidMount() {
        axios.get(process.env.REACT_APP_SERVER_API + '/public/events?limit=6')
            .then((response) => {
                const items = (response.data && response.data.items) || [];
                this.setState({ status: 'ready', items: items });
            })
            .catch((err) => {
                // Render nothing and log. This block sits on the splash page, which is the primary
                // signup surface — a dead marketing section must never take it down with it.
                console.error('Could not load public events:', err);
                this.setState({ status: 'failed', items: [] });
            });
    }

    // The six skeletons are the same height as the real cards so nothing below the section moves
    // when the data lands.
    renderSkeleton(index) {
        return (
            <div className="public-events-card public-events-card-skeleton" key={'skeleton-' + index}>
                <div className="public-events-card-media"></div>
                <div className="public-events-card-body">
                    <span className="public-events-skeleton-bar public-events-skeleton-title"></span>
                    <span className="public-events-skeleton-bar public-events-skeleton-line"></span>
                    <div className="public-events-card-footer">
                        <span className="public-events-skeleton-bar public-events-skeleton-meta"></span>
                    </div>
                </div>
            </div>
        );
    }

    renderCard(item) {
        const location = [item.event_city, item.event_state].filter(Boolean).join(', ');
        const date = this.dateParts(item.event_date_iso);
        // Zero renders nothing at all — no "0 going", no reserved space. This matches the iOS card
        // (AMEventCardView.swift:283), and it is the common case: most curated events sit at zero.
        const hasAttendees = typeof item.attendee_count === 'number' && item.attendee_count > 0;
        return (
            <Link className="public-events-card" to={'/e/' + item.public_slug} key={item.public_slug}>
                <div className="public-events-card-media">
                    {/* Same helper as the detail hero, deliberately — a card and its own detail page
                        must never show different art. Always renders: resolveEventArtwork falls back
                        to the vibe artwork when there is no event_image. */}
                    <img
                        className="public-events-card-image"
                        src={resolveEventArtwork(item)}
                        alt=""
                        onError={(event) => { event.target.style.display = 'none'; }}
                    />
                    <div className="public-events-card-scrim"></div>
                    {/* vibe_tag arrives as one string with the emoji already in it ("🎪 Convention").
                        The emoji is kept rather than stripped: it is what both apps show, and four of
                        the thirty vibe values carry a U+FE0F variation selector that naive splitting
                        mangles. text-transform: uppercase leaves the emoji untouched. */}
                    {item.vibe_tag && (
                        <span className="public-events-card-vibe">{item.vibe_tag}</span>
                    )}
                    {/* Calendar chip. Geometry is taken from the iOS one (AMEventCardView.swift:216-239
                        — 6+13+23+6 tall, min-width 46, radius 12); its colours are not, since those are
                        light-card values (#F2F2F2 well, #D32F2F month) on a card with no dark variant.
                        ⚠️ It sits top-right ON THE IMAGE rather than beside the title where iOS puts it.
                        A 3-across card is only ~223px: with the chip in the body the title dropped from
                        195px to 137px, which truncated the first two events to an identical
                        "Anime Expo LA …" — and they also share SEP 15, so the date could no longer tell
                        them apart. On the image it costs the title nothing, and it still lands inside the
                        scrim (which covers the top 45%, i.e. 67px, against the chip's 10-60px). */}
                    {date && (
                        <time className="public-events-card-date" dateTime={item.event_date_iso}>
                            <span className="public-events-card-date-month">{date.month}</span>
                            <span className="public-events-card-date-day">{date.day}</span>
                        </time>
                    )}
                </div>
                <div className="public-events-card-body">
                    <h3 className="public-events-card-title">{item.event_title}</h3>
                    {item.event_description && (
                        <p className="public-events-card-description">{item.event_description}</p>
                    )}
                    {/* Two siblings, not three items in one paragraph, because the count moves between
                        them: on mobile it joins location and time on a single line, and at >=600px it
                        detaches to the bottom-right corner while location and time stack on the left.
                        🔒 The `·` before the count is rendered INSIDE the count element, so it cannot
                        survive as a dangling separator on the 3-in-6 curated events that sit at zero. */}
                    <div className="public-events-card-footer">
                        <p className="public-events-card-meta">
                            {location && (
                                <span className="public-events-card-meta-item public-events-card-meta-location">
                                    <span role="img" aria-label="">📍</span> {location}
                                </span>
                            )}
                            {location && item.event_time && (
                                <span className="public-events-card-meta-dot">·</span>
                            )}
                            {item.event_time && (
                                <span className="public-events-card-meta-item public-events-card-meta-time">
                                    <span role="img" aria-label="">⏰</span> {item.event_time}
                                </span>
                            )}
                        </p>
                        {hasAttendees && (
                            <p className="public-events-card-attendees">
                                <span className="public-events-card-meta-dot">·</span>
                                <span className="public-events-card-attendees-value">{item.attendee_count} going</span>
                            </p>
                        )}
                    </div>
                </div>
            </Link>
        );
    }

    render() {
        const { status, items } = this.state;

        // Fetch failure, or no upcoming events: the whole section disappears, heading included.
        if (status === 'failed' || (status === 'ready' && items.length === 0)) {
            return null;
        }

        const loading = status === 'loading';
        return (
            <div className="public-events">
                <h2 className="public-events-title color-1" style={{fontFamily: "'Chelsea Market', cursive"}}>Upcoming Events</h2>
                <div className="public-events-grid">
                    {loading
                        ? [0, 1, 2, 3, 4, 5].map((index) => this.renderSkeleton(index))
                        : items.map((item) => this.renderCard(item))}
                </div>
            </div>
        );
    }
}

export default PublicEvents;
